import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const COLORS = {
  navy: "#0A1628",
  deepBlue: "#0F1A2B",
  salarBlue: "#1B6EC2",
  accent: "#00D4FF",
  gold: "#D4A843",
  white: "#F8FAFB",
  cardBg: "#1A2744",
  cardBorder: "rgba(96, 165, 250, 0.25)",
  textPrimary: "#E0F2FE",
  textBody: "#BFDBFE",
  textMuted: "#93C5FD",
};

const SectionTag = ({ children }) => (
  <div style={{
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "4px 14px", borderRadius: 20,
    background: `${COLORS.salarBlue}25`, color: COLORS.accent,
    fontSize: 12, fontWeight: 600, letterSpacing: 1.5,
    textTransform: "uppercase", marginBottom: 12,
  }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.accent }} />
    {children}
  </div>
);

export default function SpectrumStrike() {
  return (
    <div style={{ background: COLORS.navy, color: COLORS.textBody, minHeight: "100vh" }}>
      <Helmet>
        <title>Spectrum Strike Assurance Engine | SALAR Technologies</title>
        <meta
          name="description"
          content="SALAR Spectrum Strike Assurance Engine — the explainable interagency authorization layer for spectrum (DoW · NTIA · FCC · FAA · DHS) in one auditable decision."
        />
        <meta property="og:title" content="SALAR Spectrum Strike Assurance Engine | SALAR Technologies LLC" />
        <link rel="canonical" href="https://www.salartechnologies.com/spectrum-strike" />
      </Helmet>

      {/* HERO */}
      <section style={{ padding: "80px 24px 56px", maxWidth: 1200, margin: "0 auto" }}>
        <SectionTag>Capability · Tactical Spectrum</SectionTag>
        <h1 style={{
          fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 800, lineHeight: 1.15,
          color: COLORS.white, marginBottom: 18, letterSpacing: "-0.02em",
        }}>
          Spectrum Strike Assurance Engine
        </h1>
        <p style={{
          fontSize: 18, lineHeight: 1.6, color: COLORS.textBody,
          maxWidth: 820, marginBottom: 14,
        }}>
          The explainable interagency authorization layer for tactical spectrum —
          DoW · NTIA · FCC · FAA · DHS, in one auditable decision.
        </p>
        <p style={{
          fontSize: 15, lineHeight: 1.6, color: COLORS.textMuted,
          maxWidth: 820, marginBottom: 32, fontStyle: "italic",
        }}>
          Graph and constraint optimization under the hood; governance and safety on top.
          Auto-triage routine combat-net traffic, hard-block protected GNSS / safety-of-flight
          conflicts, and produce a defensible reason chain for every disposition.
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 8 }}>
          <a
            href="/spectrum-strike-demo"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="spectrum-strike-live-demo-cta"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: COLORS.salarBlue, color: COLORS.white,
              padding: "14px 28px", borderRadius: 6,
              fontSize: 15, fontWeight: 700, textDecoration: "none",
              letterSpacing: 0.4, border: `1px solid ${COLORS.accent}40`,
              boxShadow: "0 4px 16px rgba(27,110,194,0.3)",
            }}
          >
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#36C77E" }} />
            Open the Live Demo
            <span aria-hidden style={{ fontSize: 16, marginLeft: 2 }}>→</span>
          </a>
          <Link
            to="/contact?topic=Capabilities%20%26%20Partnerships&subject=Spectrum%20Strike%20Assurance%20Engine&message=I%27d%20like%20to%20discuss%20the%20Spectrum%20Strike%20Assurance%20Engine."
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "transparent", color: COLORS.accent,
              padding: "14px 28px", borderRadius: 6,
              fontSize: 15, fontWeight: 700, textDecoration: "none",
              letterSpacing: 0.4, border: `1px solid ${COLORS.accent}80`,
            }}
            data-testid="spectrum-strike-contact-cta"
          >
            Brief our team
          </Link>
        </div>
        <p style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 14, fontStyle: "italic" }}>
          The live demo is a public, illustrative interface — not operational data. No sign-in required.
        </p>
      </section>

      {/* WHAT IT DOES */}
      <section style={{ background: COLORS.deepBlue, padding: "64px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <SectionTag>What it does</SectionTag>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: COLORS.white, marginBottom: 32 }}>
            One engine. Five agencies. Every decision auditable.
          </h2>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 18,
          }}>
            {[
              {
                title: "Multi-domain rule fusion",
                body: "Adjudicates NTIA Redbook, FCC Part 2/90, FAA safety-of-flight, DHS coordination, and the Joint Restricted Frequency List in a single pass — no single-domain silo.",
              },
              {
                title: "Non-relaxable safety locks",
                body: "GNSS, GPS L2, and safety-of-flight conflicts are hard constraints. The optimizer cannot override them. Human sign-off is required and recorded.",
              },
              {
                title: "Explainable reason chain",
                body: "Every disposition exposes its ingest → graph → solve → SINR → rule → action trace, exportable as a per-record or consolidated PDF.",
              },
              {
                title: "Auto-triage at the edge",
                body: "Routine VHF combat nets and receive-only sensing clear automatically. Operators only see the requests that need them — driving queue cycle time down.",
              },
              {
                title: "Open, vendor-neutral",
                body: "Designed to connect to Spectrum XXI, EL-CID, GEMSIS, and NTIA systems via open APIs. Not locked to any one backend.",
              },
              {
                title: "Illustrative, not classified",
                body: "The public demo uses synthetic emitters, units, and grids. No live operational data is exposed.",
              },
            ].map((card, i) => (
              <div key={i} style={{
                background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`,
                borderRadius: 8, padding: 20,
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 8 }}>
                  {card.title}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: COLORS.textBody }}>
                  {card.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section style={{ padding: "64px 24px" }}>
        <div style={{
          maxWidth: 1000, margin: "0 auto",
          background: `linear-gradient(135deg, ${COLORS.cardBg}, ${COLORS.deepBlue})`,
          border: `1px solid ${COLORS.cardBorder}`, borderRadius: 10,
          padding: "40px 32px", textAlign: "center",
        }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: COLORS.white, marginBottom: 12 }}>
            See the console run cold.
          </h2>
          <p style={{ fontSize: 15, color: COLORS.textBody, marginBottom: 24, maxWidth: 640, margin: "0 auto 24px" }}>
            The live demo loads straight into the Assurance Console with REQ-1044 on SAFETY LOCK.
            Move the tactical frequency tuner, deconflict, approve, hold, deny — all client-side. No login.
          </p>
          <a
            href="/spectrum-strike-demo"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="spectrum-strike-live-demo-cta-bottom"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: COLORS.salarBlue, color: COLORS.white,
              padding: "14px 32px", borderRadius: 6,
              fontSize: 15, fontWeight: 700, textDecoration: "none",
              letterSpacing: 0.4,
            }}
          >
            Open the Live Demo
            <span aria-hidden>→</span>
          </a>
        </div>
      </section>
    </div>
  );
}
